import React, { useMemo, useState } from "react";
import { Button, Form, Input, Alert } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { useRequestAssistanceMutation } from "../../redux/features/routeQuestionApi";
import { toast } from "react-toastify";

export default function StepLocation({
  formData,
  setFormData,
  onNext,
  onBack,
  providerConfig,
}) {
  const [showFallback, setShowFallback] = useState(false);
  const [selectedAbbr, setSelectedAbbr] = useState(
    formData.selectedState?.abbr || null
  );
  const [requestAssistance, { isLoading: sending }] =
    useRequestAssistanceMutation();

  const states = useMemo(
    () => providerConfig?.states || [],
    [providerConfig]
  );

  const handleSelectState = (s) => {
    setSelectedAbbr(s.abbr);
    setFormData({
      ...formData,
      selectedState: { name: s.name, abbr: s.abbr },
    });
    setShowFallback(false);
  };

  const handleFallbackSubmit = async (values) => {
    try {
      await requestAssistance({
        name: values.name,
        phone: values.phone,
        email: values.email,
        category: formData.category,
      }).unwrap();
      toast.success(
        "Thank you. Our clinical coordination team will follow up with you."
      );
      setShowFallback(false);
    } catch (e) {
      toast.error(e?.data?.message || "Could not send request. Try again.");
    }
  };

  if (showFallback) {
    const isNew = formData.category === "newpatient";
    return (
      <div className="step-location">
        <h2 className="form-title text-white" style={{ marginBottom: 16 }}>
          {isNew ? "New Patient Information" : "Established Patients"}
        </h2>
        {isNew ? (
          <p className="text-white" style={{ marginBottom: 16, opacity: 0.95 }}>
            At this time, we are accepting new patients only in select states.
            Availability is based on current licensing and regulatory guidelines.
            If you maintain another residence in one of the states we serve, you
            may proceed using that address. If you have questions or would like to
            explore next steps, please submit your contact information below and a
            member of our team will reach out to you.
          </p>
        ) : (
          <p className="text-white" style={{ marginBottom: 16, opacity: 0.95 }}>
            Due to recent regulatory and licensing updates, appointment scheduling
            may be affected based on your current location. To request an appointment
            or receive further guidance, please submit a request through your Charm
            Health patient portal. If you are unable to access your portal, you may
            submit your information below and our clinical coordination team will
            follow up with you directly.
          </p>
        )}
        <Form layout="vertical" onFinish={handleFallbackSubmit}>
          <Form.Item
            name="name"
            label={<span className="text-white">Name</span>}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label={<span className="text-white">Phone Number</span>}
            rules={[{ required: true, message: "Phone is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={<span className="text-white">Email Address</span>}
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={sending} block>
              Request Assistance
            </Button>
          </Form.Item>
        </Form>
        <Button onClick={() => setShowFallback(false)} block className="btn-css-class">
          Back to state list
        </Button>
      </div>
    );
  }

  return (
    <div className="step-location">
      <h2 className="form-title text-white">Choose your location</h2>
      <p className="text-white" style={{ marginBottom: 20, opacity: 0.9 }}>
        Please select your state to continue. If your state is not listed, click
        &quot;I don&apos;t see my state.&quot;
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {states.map((s) => (
          <div
            key={s.abbr}
            onClick={() => handleSelectState(s)}
            className={`service-card ${selectedAbbr === s.abbr ? "selected" : ""}`}
            style={{
              position: "relative",
              padding: "12px 8px",
              textAlign: "center",
              cursor: "pointer",
              borderRadius: 8,
              border:
                selectedAbbr === s.abbr
                  ? "2px solid #fc8c02"
                  : "1px solid #444",
              background: selectedAbbr === s.abbr ? "rgba(252,140,2,0.12)" : "#1f1f1f",
            }}
          >
            {selectedAbbr === s.abbr && (
              <CheckCircleTwoTone
                twoToneColor="#fc8c02"
                style={{ marginBottom: 6, fontSize: 20 }}
              />
            )}
            <div style={{ fontWeight: 600, color: "#fff" }}>{s.name}</div>
            <div style={{ fontSize: 12, color: "#999" }}>{s.abbr}</div>
          </div>
        ))}
      </div>

      <Alert
        type="info"
        showIcon
        message={
          <span style={{ color: "#000" }}>
            Don&apos;t see your state? Use the button below so our team can help.
          </span>
        }
        style={{ marginBottom: 16 }}
      />

      <Button
        type="default"
        block
        className="btn-css-class"
        style={{ marginBottom: 16 }}
        onClick={() => setShowFallback(true)}
      >
        I don&apos;t see my state
      </Button>

      <div style={{ display: "flex", gap: "1rem" }}>
        <Button className="btn-css-class" onClick={onBack} block>
          Back
        </Button>
        <Button
          type="primary"
          size="large"
          block
          disabled={!selectedAbbr}
          onClick={onNext}
          className="btn-css"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
